import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import { useGetList, useNotify } from 'react-admin';
import client from 'src/data/clientApiClient';
import { CompanySubscriptionSetRecord, ExternalApiClientRecord } from 'src/types';
import { CLIENT_API_API_KEY_HEADER_NAME, IS_LOCAL_OR_QA_ENV } from 'src/utils/defaults/Constants';
import { useEffect, useState } from 'react';

/** Staging subscription set list; includes companyIds and dataPackages for sync-to-QA. */
const clientCompanySubscriptionSetsGQL = gql`
  query Client {
    client {
      companySubscriptionSets {
        id
        name
        description
        companyLimit
        allCompanies
        companyIds
        dataPackages {
          id
        }
      }
    }
  }
`;

/** Staging API subscription set shape (includes companyIds and dataPackages for sync). */
export type StagingSubscriptionSet = {
  id: string;
  name: string;
  description?: string | null;
  companyLimit?: number | null;
  allCompanies: boolean;
  companyIds?: string[] | null;
  dataPackages: { id: string }[];
};

interface UseCompanySubscriptionSetsResult {
  companySubscriptionSets: StagingSubscriptionSet[] | CompanySubscriptionSetRecord[];
  isLoading: boolean;
  isQaOrgWithoutStagingClient: boolean;
  companySubscriptionSetsFromClientApiData:
    | { client: { companySubscriptionSets: StagingSubscriptionSet[] } }
    | undefined;
}

/**
 * Fetches company subscription sets for a given organization.
 * Handles QA vs production environments (QA fetches from Staging Client API,
 * production fetches from Admin API via external API client).
 */
export const useCompanySubscriptionSets = (companyDataApiKey?: string): UseCompanySubscriptionSetsResult => {
  const notify = useNotify();
  const [isQaOrgWithoutStagingClient, setIsQaOrgWithoutStagingClient] = useState(false);

  // (QA env only) fetch subscription sets from Client API
  // TODO: Once Portal starts using QA Client API, use Admin API if this view isn't moved
  const {
    data: companySubscriptionSetsFromClientApiData,
    loading: companySubscriptionSetsFromClientApiLoading,
    error: companySubscriptionSetsFromClientApiError,
  } = useQuery<{ client: { companySubscriptionSets: StagingSubscriptionSet[] } }>(clientCompanySubscriptionSetsGQL, {
    client,
    // this query is only needed in the local and QA envs, to get subscription sets from Staging
    skip: !IS_LOCAL_OR_QA_ENV || !companyDataApiKey,
    context: {
      headers: { [CLIENT_API_API_KEY_HEADER_NAME]: companyDataApiKey },
    },
    // we don't want to cache this response, as the request headers are set for each org. that is
    // loaded, but Apollo Client doesn't consider the headers when resolving a cache key
    fetchPolicy: 'no-cache',
  });

  const clientApiNetworkErrorMessage = companySubscriptionSetsFromClientApiError?.networkError?.message;

  useEffect(() => {
    if (!clientApiNetworkErrorMessage) return;

    console.error(companySubscriptionSetsFromClientApiError);
    // If we get a 401 error from Client API, we'll assume that the key assigned to this org.
    // doesn't match an active API client (from Staging Client API).
    if (clientApiNetworkErrorMessage.includes('401')) {
      setIsQaOrgWithoutStagingClient(true);
    } else {
      notify('Error fetching API client data', { type: 'error' });
    }
  }, [clientApiNetworkErrorMessage]);

  const {
    data: externalApiClientArr,
    isLoading: externalApiClientLoading,
    error: externalApiClientError,
  } = useGetList<ExternalApiClientRecord>(
    'external_api_clients',
    {
      filter: {
        'token@_eq': companyDataApiKey,
        'archived_at@_is_null': true,
      },
      pagination: { page: 1, perPage: 1 },
    },
    // we don't reference this in QA while QA orgs use Staging API clients
    { enabled: !IS_LOCAL_OR_QA_ENV && !!companyDataApiKey },
  );
  // should only return 1 record
  const externalApiClient = externalApiClientArr?.[0];

  // fetch subscription sets from Craft Admin API
  const {
    data: companySubscriptionSetsFromAdminAPI,
    isLoading: companySubscriptionSetsFromAdminAPILoading,
    error: companySubscriptionSetsFromAdminAPIError,
  } = useGetList<CompanySubscriptionSetRecord>(
    'company_subscription_sets',
    {
      filter: {
        external_api_client_id: externalApiClient?.id,
        all_companies: false,
        'deleted_at@_is_null': true,
      },
      meta: { noLimit: true },
    },
    // we get this data from a different query in QA while QA orgs use Staging API clients
    { enabled: !IS_LOCAL_OR_QA_ENV && !!externalApiClient },
  );

  const adminApiErrorMessage = externalApiClientError?.message ?? companySubscriptionSetsFromAdminAPIError?.message;

  useEffect(() => {
    if (!adminApiErrorMessage) return;

    console.error(externalApiClientError ?? companySubscriptionSetsFromAdminAPIError);
    notify('Error fetching company subscription set data', { type: 'error' });
  }, [adminApiErrorMessage]);

  /**
   * As QA orgs use Staging API clients, we have to fetch Company Subscription Sets from Staging
   * Client API for those orgs. If the current env is `local` or `qa`, then
   * `companySubscriptionSetsFromClientApiData` should be defined and used. Otherwise -
   * `companySubscriptionSetsFromAdminAPI` should be defined and used.
   * TODO: rm once QA Portal starts using QA Client API
   */
  const companySubscriptionSets =
    companySubscriptionSetsFromAdminAPI ??
    companySubscriptionSetsFromClientApiData?.client.companySubscriptionSets ??
    [];

  const isLoading =
    companySubscriptionSetsFromClientApiLoading ||
    externalApiClientLoading ||
    companySubscriptionSetsFromAdminAPILoading;

  return { companySubscriptionSets, isLoading, isQaOrgWithoutStagingClient, companySubscriptionSetsFromClientApiData };
};
